type LogLevel = 'debug' | 'info' | 'warn' | 'error';

type DatadogLogger = {
    debug?: (message: string, context?: Record<string, unknown>) => void;
    info?: (message: string, context?: Record<string, unknown>) => void;
    warn?: (message: string, context?: Record<string, unknown>) => void;
    error?: (message: string, context?: Record<string, unknown>) => void;
};

type CreateLoggerOptions = {
    scope: string;
    env?: string;
    service?: string;
    datadogLogger?: DatadogLogger;
};

type ConsoleLikeArg = unknown;
type LogContext = Record<string, unknown>;
type LogMetadata = {
    event?: string;
    message?: string;
    context?: LogContext;
    error?: unknown;
    onceKey?: string;
};

const LOG_METADATA_KEYS = new Set(['event', 'message', 'context', 'error', 'onceKey']);
const emittedOnceKeys = new Set<string>();

const toErrorContext = (err: unknown): Record<string, unknown> | undefined => {
    if (!err) return undefined;

    if (err instanceof Error) {
        return {
            name: err.name,
            message: err.message,
            stack: err.stack,
        };
    }

    return { value: String(err) };
};

const isPlainObject = (value: unknown): value is LogContext => {
    if (!value || typeof value !== 'object') return false;
    if (value instanceof Error) return false;

    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
};

const splitContextObject = (value: LogContext) => {
    const metadata: LogMetadata = {};
    const context: LogContext = {};

    for (const [key, entry] of Object.entries(value)) {
        if (LOG_METADATA_KEYS.has(key)) {
            if (key === 'event' && typeof entry === 'string') metadata.event = entry;
            else if (key === 'message' && typeof entry === 'string') metadata.message = entry;
            else if (key === 'context' && isPlainObject(entry)) metadata.context = entry;
            else if (key === 'error') metadata.error = entry;
            else if (key === 'onceKey' && typeof entry === 'string') metadata.onceKey = entry;
            continue;
        }

        context[key] = entry;
    }

    return { metadata, context };
};

const emitConsole = (level: LogLevel, message: string, payload: Record<string, unknown>) => {
    if (!import.meta.dev && level !== 'warn' && level !== 'error') return;

    const method = console[level] ?? console.log;
    method(`[${payload.scope}] ${message}`, payload);
};

const emitDatadog = (
    datadogLogger: DatadogLogger | undefined,
    level: LogLevel,
    message: string,
    payload: Record<string, unknown>
) => {
    if (!datadogLogger) return;
    if (level !== 'warn' && level !== 'error') return;

    try {
        const method = datadogLogger[level];
        if (typeof method === 'function') {
            method.call(datadogLogger, message, payload);
            return;
        }

        datadogLogger.error?.call(datadogLogger, message, payload);
    } catch (err) {
        emitConsole('error', 'Failed to forward log to Datadog', {
            scope: 'logger',
            error: toErrorContext(err),
            originalLevel: level,
            originalMessage: message,
        });
    }
};

let sharedConfig: CreateLoggerOptions = {
    scope: 'app',
    service: 'live-instant-rips-ui',
};

export const configureLogger = (options: Partial<CreateLoggerOptions>) => {
    sharedConfig = {
        ...sharedConfig,
        ...options,
    };
};

const normalize = (
    args: ConsoleLikeArg[]
): {
    message: string;
    event?: string;
    context: LogContext;
    error?: Record<string, unknown>;
    onceKey?: string;
} => {
    if (args.length === 0) {
        return { message: '', context: {} };
    }

    const maybeMessage = args[0];
    const hasStringMessage = typeof maybeMessage === 'string';
    const message = hasStringMessage ? maybeMessage : String(maybeMessage);
    const restArgs = hasStringMessage ? args.slice(1) : args;

    const extraArgs: ConsoleLikeArg[] = [];
    const context: LogContext = {};
    let event: string | undefined;
    let messageOverride: string | undefined;
    let explicitError: unknown;
    let firstErrorArg: unknown;
    let onceKey: string | undefined;

    restArgs.forEach((arg) => {
        if (arg instanceof Error) {
            explicitError ??= arg;
            firstErrorArg ??= arg;
            return;
        }

        if (isPlainObject(arg)) {
            const { metadata, context: objectContext } = splitContextObject(arg);
            if (metadata.event) event = metadata.event;
            if (metadata.message) messageOverride = metadata.message;
            if (metadata.error !== undefined) explicitError = metadata.error;
            if (metadata.onceKey) onceKey = metadata.onceKey;
            if (metadata.context) Object.assign(context, metadata.context);
            Object.assign(context, objectContext);
            return;
        }

        extraArgs.push(arg);
    });

    if (extraArgs.length > 0) {
        context.args = extraArgs;
    }

    return {
        message: messageOverride ?? message,
        event,
        context,
        error: toErrorContext(explicitError ?? firstErrorArg),
        onceKey,
    };
};

const createWriter = (scopeOverride?: string) => {
    return (level: LogLevel, options: { once?: boolean } = {}, ...args: ConsoleLikeArg[]) => {
        const normalized = normalize(args);
        const scope = scopeOverride ?? sharedConfig.scope;
        const payload = {
            scope,
            env: sharedConfig.env,
            service: sharedConfig.service,
            level,
            event: normalized.event ?? `${scope}.${level}`,
            timestamp: new Date().toISOString(),
            context: normalized.context,
            error: normalized.error,
        };

        if (options.once) {
            const dedupeKey = normalized.onceKey ?? `${scope}:${payload.event}:${normalized.message}`;
            if (emittedOnceKeys.has(dedupeKey)) return;
            emittedOnceKeys.add(dedupeKey);
        }

        emitConsole(level, normalized.message, payload);
        emitDatadog(sharedConfig.datadogLogger, level, normalized.message, payload);
    };
};

const createBoundLogger = (scopeOverride?: string) => {
    const write = createWriter(scopeOverride);
    return {
        log: (...args: ConsoleLikeArg[]) => write('info', {}, ...args),
        debug: (...args: ConsoleLikeArg[]) => write('debug', {}, ...args),
        info: (...args: ConsoleLikeArg[]) => write('info', {}, ...args),
        warn: (...args: ConsoleLikeArg[]) => write('warn', {}, ...args),
        warnOnce: (...args: ConsoleLikeArg[]) => write('warn', { once: true }, ...args),
        error: (...args: ConsoleLikeArg[]) => write('error', {}, ...args),
    };
};

export const logger = createBoundLogger();

export const createLogger = (scope: string) => createBoundLogger(scope);
